import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Tag, ArrowRight, Crown } from 'lucide-react';
import clsx from 'clsx';
import { useFeaturedProducts } from '../../services/productService';
import { useFeaturedTeam } from '../../services/teamService';
import { getImageUrl } from '../../lib/supabase';
import { formatPrice } from '../../lib/utils';
import { HERO_IMAGE } from '../../lib/constants';
import { StarRating } from '../StarRating/StarRating';
import { CategoryBar } from '../CategoryBar/CategoryBar';

/**
 * Landing-page hero — auto-rotating product image carousel.
 * Bottom-right overlay shows product name, rating, category, price & CTA.
 * Search bar lives in the Navbar, not here.
 */
export function Hero() {
  const { data: products, isLoading } = useFeaturedProducts();
  const { data: teamMembers } = useFeaturedTeam();
  const [current, setCurrent] = useState(0);

  const slides = (products && products.length > 0) ? products : null;
  const slideCount = slides ? slides.length : 0;

  const next = useCallback(() => {
    setCurrent((c) => (c === slideCount - 1 ? 0 : c + 1));
  }, [slideCount]);

  // Auto-advance every 5 s
  useEffect(() => {
    if (slideCount <= 1) return;
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [slideCount, next]);

  // Reset index when products load
  useEffect(() => {
    setCurrent(0);
  }, [products]);

  /* ── Loading / fallback state ── */
  if (isLoading || !slides) {
    return (
      <>
        <section className="relative min-h-[50vh] sm:min-h-[60vh] lg:min-h-[75vh] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={HERO_IMAGE}
              alt="Restaurant"
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-brand-dark/50" />
          </div>
          {/* Skeleton overlay */}
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 flex justify-end">
            <div className="w-full sm:w-96 space-y-3 animate-pulse">
              <div className="h-6 bg-brand-white/20 rounded w-3/4" />
              <div className="h-4 bg-brand-white/20 rounded w-1/2" />
              <div className="h-4 bg-brand-white/20 rounded w-1/3" />
              <div className="h-10 bg-brand-white/20 rounded w-32 mt-2" />
            </div>
          </div>
        </section>
        <CategoryBar />
      </>
    );
  }

  const product = slides[current];
  const imageSrc = getImageUrl(product.image_path);
  const categoryName = product.categories?.name;
  const slug = product.slug;

  return (
    <>
      <section className="relative min-h-[50vh] sm:min-h-[60vh] lg:min-h-[75vh] flex items-end overflow-hidden">
        {/* ── Background image carousel ── */}
        <AnimatePresence mode="wait">
          <motion.img
            key={product.id}
            src={imageSrc}
            alt={product.title}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0 w-full h-full object-cover z-[1]"
          />
        </AnimatePresence>

        {/* Dark gradient — stronger at bottom-right for readability */}
        <div className="absolute inset-0 bg-gradient-to-tl from-brand-dark/85 via-brand-dark/40 to-transparent z-[2]" />

        {/* ── Team strip — left side, above dots ── */}
        {teamMembers?.length > 0 && (
          <div className="absolute left-4 sm:left-6 bottom-16 sm:bottom-20 z-20">
            <div className="flex items-center gap-3">
              {/* Stacked avatars */}
              <div className="flex -space-x-2">
                {teamMembers.slice(0, 4).map((m) => {
                  const mImg = m.image_path ? getImageUrl(m.image_path) : null;
                  return (
                    <div
                      key={m.id}
                      className="w-9 h-9 rounded-full border-2 border-brand-white overflow-hidden bg-brand-light shadow-md"
                      title={`${m.full_name} — ${m.role}`}
                    >
                      {mImg ? (
                        <img src={mImg} alt={m.full_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-brand-red bg-brand-red/10">
                          {m.full_name?.charAt(0)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Label */}
              <div className="hidden sm:block">
                <p className="text-xs font-bold text-brand-white drop-shadow-lg leading-tight">
                  {teamMembers[0]?.full_name}
                  {teamMembers.length > 1 && (
                    <span className="text-brand-white/70 font-medium"> +{teamMembers.length - 1}</span>
                  )}
                </p>
                <p className="text-[10px] text-brand-white/70 drop-shadow flex items-center gap-1">
                  {(teamMembers[0]?.role?.toLowerCase().includes('owner') || teamMembers[0]?.role?.toLowerCase().includes('founder')) && (
                    <Crown size={9} className="text-warning" />
                  )}
                  {teamMembers[0]?.role}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Dot indicators — left side, horizontal ── */}
        {slideCount > 1 && (
          <div className="absolute left-4 sm:left-6 bottom-6 sm:bottom-8 flex items-center gap-2.5 z-20">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={clsx(
                  'rounded-full transition-all duration-300 border-2',
                  i === current
                    ? 'w-9 h-3.5 bg-brand-white border-brand-white shadow-lg shadow-white/30'
                    : 'w-3.5 h-3.5 bg-brand-white/60 border-brand-white/60 hover:bg-brand-white hover:border-brand-white'
                )}
                aria-label={`Go to product ${i + 1}`}
              />
            ))}
          </div>
        )}

        {/* ── Bottom-right product info overlay ── */}
        <AnimatePresence mode="wait">
          <motion.div
            key={product.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
            className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8 flex justify-end"
          >
            <div className="w-full sm:w-auto sm:max-w-md text-right">
              {/* Product name */}
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-brand-white leading-tight drop-shadow-lg">
                {product.title}
              </h2>

              {/* Rating + category */}
              <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 mt-2">
                <div className="flex items-center gap-1.5">
                  <StarRating rating={5} size={16} />
                  <span className="text-sm font-semibold text-brand-white/90 drop-shadow">5.0</span>
                </div>
                {categoryName && (
                  <span className="flex items-center gap-1 text-sm text-brand-white/80 drop-shadow">
                    <Tag size={13} />
                    {categoryName}
                  </span>
                )}
              </div>

              {/* Price */}
              <p className="mt-3 text-2xl sm:text-3xl font-extrabold text-brand-white drop-shadow-lg">
                {formatPrice(product.price)}
              </p>

              {/* Short description */}
              {product.short_description && (
                <p className="mt-2 text-sm text-brand-white/80 line-clamp-2 leading-relaxed drop-shadow">
                  {product.short_description}
                </p>
              )}

              {/* CTA arrow */}
              <Link
                to={`/menu/${slug}`}
                className="mt-4 inline-flex items-center gap-2 text-brand-white/90 hover:text-brand-red text-sm font-bold transition-colors group"
              >
                Explore
                <ArrowRight size={24} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>
      </section>

      {/* Category icons row below hero */}
      <CategoryBar />
    </>
  );
}
